import * as c3 from 'c3'
import * as d3 from 'd3'
import { getSpotSummaries, getSpotSummariesDetail } from './spotSummaryApi'

const TIME_SLOT_NUMBER = 48
const areas = [
  { key: 'system', name: 'システム', color: '#FFFFFF' },
  { key: 'hepco', name: '北海道', color: '#50FE86' },
  { key: 'tohoku_epco', name: '東北', color: '#FFC33B' },
  { key: 'tepco', name: '東京', color: '#FD4174' },
  { key: 'chuden', name: '中部', color: '#3B7FFA' },
  { key: 'rikuden', name: '北陸', color: '#F86E5A' },
  { key: 'kepco', name: '関西', color: '#86FFEC' },
  { key: 'energia', name: '中国', color: '#BA1FEA' },
  { key: 'yonden', name: '四国', color: '#FFFD38' },
  { key: 'kyuden', name: '九州', color: '#F68EFB' },
]
export const areaHash = Object.fromEntries(areas.map((area) => [area.key, area.name]))
const areaKeys = Object.keys(areaHash)

const drawC3Chart = (elementId, columns, colors, format) => {
  // グラフ生成
  c3.generate({
    bindto: elementId,
    padding: {
      right: 10,
      left: 45,
      bottom: 20,
    },
    data: {
      x: 'x',
      columns: columns,
      colors: colors,
    },
    axis: {
      x: {
        type: 'timeseries',
        localtime: true,
        tick: {
          format: format,
          culling: { max: 7 },
        },
      },
      y: {
        tick: {
          format: d3.format('.2f'),
        },
      },
    },
    grid: {
      x: {
        show: true,
      },
      y: {
        show: true,
      },
    },
  })
}

const getTimeLine = (startDate, endDate, type) => {
  const startTime = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    0,
    0
  )
  const endTarget = type === 'normal' ? endDate : startDate
  const endTime = new Date(
    endTarget.getFullYear(),
    endTarget.getMonth(),
    endTarget.getDate(),
    23,
    59
  )
  const result = []
  for (let i = 0; startTime.getTime() + 30 * 60 * 1000 * i < endTime.getTime(); ++i) {
    const time = new Date(startTime.getTime() + 30 * 60 * 1000 * i)
    result.push(time)
  }

  return result
}

const getDetailTimeLine = (startDate, endDate) => {
  const startTime = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    0,
    0
  )
  const endTime = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59)

  const result = []
  for (let i = 0; startTime.getTime() + 24 * 60 * 60 * 1000 * i < endTime.getTime(); ++i) {
    const time = new Date(startTime.getTime() + 24 * 60 * 60 * 1000 * i)
    result.push(time)
  }
  return result
}

export const setOverviewChart = async (elementId, dateParams) => {
  const result = await getSpotSummaries(dateParams.startDate, dateParams.endDate)
  if (!result) {
    throw new Error('Failed getSpotSummaries')
  }

  const startDate = dateParams.startDate
  const endDate = dateParams.endDate
  const chartType = dateParams.chartType

  const timeLine = getTimeLine(startDate, endDate, chartType)
  const format = chartType === 'normal' ? '%m/%d %H:%M' : '%H:%M'

  const spotSummaryData = {}
  if (chartType === 'normal') {
    areaKeys.forEach((key) => {
      spotSummaryData[key] = result.spot_summaries.map((x) => x[key])
    })
    if (timeLine.length !== spotSummaryData.system.length) {
      const diffLength = timeLine.length - spotSummaryData.system.length
      const slot = [...Array(diffLength)].map((_) => null)
      Object.keys(spotSummaryData).forEach((key) => {
        spotSummaryData[key] = spotSummaryData[key].concat(slot)
      })
    }
  } else {
    const spotSummaries = {}
    areaKeys.forEach((key) => {
      spotSummaries[key] = result.spot_summaries.map((x) => x[key])
    })

    Object.entries(spotSummaries).forEach(([areaKey, dataList]) => {
      const sumSlot = [...Array(48)].map((_) => 0.0)
      const dayNumber = dataList.length / TIME_SLOT_NUMBER

      for (let i = 0; i < dayNumber; i++) {
        for (let j = 0; j < TIME_SLOT_NUMBER; j++) {
          sumSlot[j] += parseFloat(dataList[j + i * TIME_SLOT_NUMBER])
        }
      }
      // NOTE: 平均値を算出して、小数点第三位で四捨五入
      const averageList = sumSlot.map((x) => Math.round((x / dayNumber) * 100) / 100)
      spotSummaryData[areaKey] = averageList
    })
  }

  const columns = [
    ['x', ...timeLine],
    ['システム', ...spotSummaryData.system],
    ['北海道', ...spotSummaryData.hepco],
    ['東北', ...spotSummaryData.tohoku_epco],
    ['東京', ...spotSummaryData.tepco],
    ['中部', ...spotSummaryData.chuden],
    ['北陸', ...spotSummaryData.rikuden],
    ['関西', ...spotSummaryData.kepco],
    ['中国', ...spotSummaryData.energia],
    ['四国', ...spotSummaryData.yonden],
    ['九州', ...spotSummaryData.kyuden],
  ]

  const colors = Object.fromEntries(areas.map((area) => [area.name, area.color]))
  drawC3Chart(elementId, columns, colors, format)
}

export const setDetailChart = async (elementId, dateParams) => {
  const result = await getSpotSummariesDetail(
    dateParams.startDate,
    dateParams.endDate,
    dateParams.area
  )
  if (!result) {
    throw new Error('Failed getSpotSummariesDetail')
  }

  const startDate = dateParams.startDate
  const endDate = dateParams.endDate

  const timeLine = getDetailTimeLine(startDate, endDate)

  const spotSummaries = result.spot_summaries

  // drawDetailChart(elementId, timeLine, spotSummaries)
  const columns = [
    ['x', ...timeLine],
    ['高値', ...spotSummaries.max],
    ['低値', ...spotSummaries.min],
    ['平均', ...spotSummaries.average],
  ]
  const colors = {
    高値: '#ff0037',
    低値: '#5ac0f8',
    平均: '#5af86b',
  }
  const format = '%m/%d'
  drawC3Chart(elementId, columns, colors, format)
}
